import React, { useState, useEffect } from 'react';
import CharacterDialog from '../../components/CharacterDialog/CharacterDialog';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import './CreateBook.css';
import { Character } from '../../types/character';
import { charactersAPI } from '../../api/characters';
import { useNavigate } from 'react-router-dom';

const CreateBook = () => {
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    characters: [] as Character[]
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errors, setErrors] = useState({
    title: '',
    description: ''
  });

  const handleAddCharacter = () => {
    setIsDialogOpen(true);
  };

  const handleCharacterSelect = async (character: Character) => {
    try {
      await charactersAPI.select(character.id);
      setFormData(prev => ({
        ...prev,
        characters: [...prev.characters, character]
      }));
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to save character selection:', error);
      // Handle error (show error message to user)
    }
  };

  const handleCharacterRemove = (characterId: string) => {
    setFormData(prev => ({
      ...prev,
      characters: prev.characters.filter(c => c.id !== characterId)
    }));
  };

  const handleNext = () => {
    const trimmedTitle = formData.title.trim();
    const trimmedDescription = formData.description.trim();
    let hasErrors = false;
    
    // Reset errors
    setErrors({
      title: '',
      description: ''
    });

    if (trimmedTitle.length < 5) {
      setErrors(prev => ({
        ...prev,
        title: 'Title must be at least 5 characters long.'
      }));
      hasErrors = true;
    }

    if (!trimmedDescription) {
      setErrors(prev => ({
        ...prev,
        description: 'Description must be at least 1 character long.'
      }));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    navigate('/create/2-preferences', { 
      state: {
        ...formData,
        story_preferences: {
          is_public: true,
          longer_text: false,
          rhyming_story: false,
          page_count: 4
        }
      }
    });
  };

  const handleInputChange = (field: 'title' | 'description', value: string) => {
    const trimmedValue = value.trim();
    
    // Clear error when validation passes
    if (field === 'title' && trimmedValue.length >= 5) {
      setErrors(prev => ({ ...prev, title: '' }));
    }
    if (field === 'description' && trimmedValue.length >= 1) {
      setErrors(prev => ({ ...prev, description: '' }));
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="write-book">
      {/* Hero title section with background */}
      <div className="title-hero-wrapper">
        <div className="title-container">
          <div className="self-stretch text-center justify-start text-fuchsia-500 text-3xl font-normal font-['Combo'] tracking-wide">Create a Book</div>
          <div className="self-stretch text-center justify-start text-black text-5xl font-medium font-['Baloo_2'] leading-[66px]">Create your personalized ai storybook!</div>
        </div>
      </div>
      
      {/* Progress Bar Section */}
      <ProgressBar currentStep={1} />
      
      <div className="book-form-container">
        <div className="form-card">
          {/* Video Player Section */}
          <div className="video-container" style={{ display: 'none' }}>
            <div className="video-overlay"></div>
            <div className="video-header">
              <div className="video-avatar">
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="22" cy="22" r="22" fill="#D9D9D9"/>
                </svg>
              </div>
              <div className="video-title">How to create a Book?</div>
            </div>
            <div className="video-player">
              <img src="/default-avatar.png" alt="Book preview" className="video-image" />
              <div className="play-button">
                <svg width="74" height="74" viewBox="0 0 74 74" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="37" cy="37" r="37" fill="#E13EED" fillOpacity="0.5"/>
                  <path d="M50 37L30 48.6603L30 25.3397L50 37Z" fill="white"/>
                </svg>
              </div>
            </div>
          </div>
          
          {/* Info Box */}
          <div className="info-box-new">
            <div className="info-icon-new">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_1_98450)">
                  <path d="M9.99935 18.3334C14.6017 18.3334 18.3327 14.6025 18.3327 10.0001C18.3327 5.39771 14.6017 1.66675 9.99935 1.66675C5.39698 1.66675 1.66602 5.39771 1.66602 10.0001C1.66602 14.6025 5.39698 18.3334 9.99935 18.3334Z" stroke="#E13EED" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 13.3333V10" stroke="#E13EED" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 6.66663H10.0083" stroke="#E13EED" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_1_98450">
                    <rect width="20" height="20" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="info-text">
              <span className="text-regular">Both </span>
              <span className="text-bold">title</span>
              <span className="text-regular"> and </span>
              <span className="text-bold">description</span>
              <span className="text-regular"> will impact your story, they can be as short and as detailed as you want, you can write a few words or a whole outline it's up to you. Regardless of that the book will have </span>
              <span className="text-bold">4-12 pages</span>
              <span className="text-regular"> - you can set it at the last step if you are subscribed.</span>
            </div>
            </div>

          {/* Form Fields */}
          <div className="form-fields">
            {/* Title Field */}
            <div className="form-group-new">
              <label className="form-label">Title</label>
              <div className="input-container">
                <input
                  type="text"
                  placeholder="Enter title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={errors.title ? 'form-input-error' : 'form-input'}
                />
              </div>
                {errors.title && <div className="error-message">{errors.title}</div>}
              <div className="examples-container">
                <div className="example-label">Example :</div>
                <div className="example-list">
                  <div className="example-item">1. A mystery in the closet</div>
                  <div className="example-item">2. A day on a beach</div>
                  <div className="example-item">3. Adventures in the secret garden</div>
                </div>
                </div>
              </div>

            {/* Description Field */}
            <div className="form-group-new">
              <label className="form-label">Description</label>
              <div className="textarea-container">
                <textarea
                  placeholder="Enter description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className={errors.description ? 'form-textarea-error' : 'form-textarea'}
                  rows={6}
                />
              </div>
                {errors.description && <div className="error-message">{errors.description}</div>}
              <div className="examples-container">
                <div className="example-label">Example :</div>
                <div className="example-list">
                  <div className="example-item">1. Boy is scared of the monster in the closet but it is just a shadow</div>
                  <div className="example-item">2. The kids have a contest for the greatest sand castle</div>
                  <div className="example-item">3. Girl visits grandparent in the village, she finds a garden which is full of secrets and adventures</div>
                </div>
              </div>
                      </div>
            
            {/* Add Character */}
            <div className="character-container">
              <div className="character-label">Character</div>
              
              {/* Display selected characters */}
              {formData.characters.map(character => (
                <div key={character.id} className="character-item">
                  <div className="character-avatar">
                    <img src={character.image || '/default-avatar.png'} alt={character.name} />
                  </div>
                  <div className="character-name">{character.name}</div>
                  <div className="character-actions">
                    {/* <button className="edit-btn" onClick={() => console.log('edit')}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.7167 7.51667L12.4833 8.28333L4.93333 15.8333H4.16667V15.0667L11.7167 7.51667ZM14.7167 2.5C14.5083 2.5 14.2917 2.58333 14.1333 2.74167L12.6083 4.26667L15.7333 7.39167L17.2583 5.86667C17.5833 5.54167 17.5833 5.01667 17.2583 4.69167L15.3083 2.74167C15.1417 2.575 14.9333 2.5 14.7167 2.5ZM11.7167 5.15833L2.5 14.375V17.5H5.625L14.8417 8.28333L11.7167 5.15833Z" fill="#4785FF"/>
                      </svg>
                    </button> */}
                    <button className="delete-btn" onClick={() => handleCharacterRemove(character.id)}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 15.8333C5 16.75 5.75 17.5 6.66667 17.5H13.3333C14.25 17.5 15 16.75 15 15.8333V5.83333H5V15.8333ZM15.8333 3.33333H12.9167L12.0833 2.5H7.91667L7.08333 3.33333H4.16667V5H15.8333V3.33333Z" fill="#FF4747"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              
              <div className="character-box" onClick={handleAddCharacter}>
                <div className="character-icon">
                  <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_1_98443)">
                      <path d="M16.272 15.4145C18.3896 15.4145 20.2233 14.6549 21.7216 13.1564C23.2199 11.6581 23.9794 9.8249 23.9794 7.70703C23.9794 5.58979 23.2199 3.75636 21.7214 2.25755C20.2228 0.759497 18.3893 0 16.272 0C14.154 0 12.3207 0.759497 10.8225 2.2578C9.32423 3.75611 8.56442 5.5896 8.56442 7.70703C8.56442 9.8249 9.32417 11.6584 10.8227 13.1567C12.3213 14.6548 14.1548 15.4145 16.272 15.4145ZM29.758 24.6063C29.7147 23.9828 29.6273 23.3026 29.4987 22.5844C29.3688 21.8607 29.2016 21.1767 29.0013 20.5514C28.7946 19.9052 28.5133 19.267 28.1657 18.6554C27.8048 18.0207 27.381 17.4679 26.9054 17.0131C26.4081 16.5373 25.7992 16.1547 25.0951 15.8756C24.3935 15.5981 23.6159 15.4575 22.7841 15.4575C22.4574 15.4575 22.1416 15.5915 21.5314 15.9887C21.0977 16.2712 20.6628 16.5516 20.2265 16.83C19.8073 17.0971 19.2394 17.3474 18.538 17.5739C17.8537 17.7954 17.1589 17.9077 16.4731 17.9077C15.7873 17.9077 15.0927 17.7954 14.4076 17.5739C13.707 17.3476 13.1392 17.0974 12.7204 16.8302C12.2348 16.5199 11.7954 16.2367 11.4143 15.9884C10.8049 15.5913 10.4887 15.4572 10.1621 15.4572C9.33004 15.4572 8.55273 15.5981 7.8513 15.8759C7.14768 16.1545 6.53856 16.5371 6.04074 17.0134C5.56543 17.4684 5.14137 18.0209 4.781 18.6554C4.43363 19.267 4.15238 19.9049 3.94532 20.5517C3.74532 21.1769 3.57813 21.8607 3.44825 22.5844C3.31957 23.3017 3.23219 23.9821 3.18894 24.6071C3.14592 25.236 3.12459 25.8663 3.12501 26.4967C3.12501 28.1671 3.656 29.5194 4.70312 30.5167C5.73731 31.5009 7.10568 32.0001 8.76973 32.0001H24.1779C25.842 32.0001 27.2099 31.5011 28.2443 30.5168C29.2917 29.5201 29.8227 28.1676 29.8227 26.4965C29.8224 25.8517 29.8007 25.2157 29.758 24.6063Z" fill="#666666"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_1_98443">
                        <rect width="32" height="32" fill="white" transform="translate(0.5)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div className="character-add-text">Add Character</div>
              </div>
            </div>
          </div>
          
          {/* Next Button */}
          <div className="next-button-container">
            <button className="next-button" onClick={handleNext}>Next</button>
          </div>
        </div>
      </div>

      {isDialogOpen && (
        <CharacterDialog
          onClose={() => setIsDialogOpen(false)}
          onSelectCharacter={(character: Character) => {
            const updatedCharacter = {
              ...character,
            } as Character;
            handleCharacterSelect(updatedCharacter);
          }}
          selectedCharacters={formData.characters}
        />
      )}
    </div>
  );
};

export default CreateBook;